import { useEffect, useState } from "react";
import { buildStrategyNextActionPath, normalizeStrategyNextAction } from "../utils/strategyNextAction";

// A request identity, not a cached governance decision or a financial digest.
// Rehydration often constructs fresh objects with unchanged contents.
const stable = value => Array.isArray(value) ? value.map(stable) : value && typeof value === "object"
  ? Object.fromEntries(Object.keys(value).sort().map(key => [key, stable(value[key])])) : value;

export default function useStrategyCurrency({ baseUrl, campaignId, advertiserId, token, authScope, recordVersion, provenance, loading, ready }) {
  const key = JSON.stringify(stable({ campaignId, advertiserId, token, authScope, recordVersion, provenance }));
  const [result, setResult] = useState(null);
  useEffect(() => {
    setResult(null);
    if (!ready || loading || !campaignId || !baseUrl || !token) return undefined;
    let active = true;
    const controller = new AbortController();
    fetch(`${baseUrl.replace(/\/+$/, "")}${buildStrategyNextActionPath(campaignId)}`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      cache: "no-store", signal: controller.signal,
    }).then(async response => {
      const next = response.ok ? normalizeStrategyNextAction(await response.json()) : null;
      if (next && String(next.campaignId) !== String(campaignId)) throw Error("Campaign identity mismatch");
      if (active) setResult({ key, next, status: next ? "available" : "unavailable" });
    }).catch(() => { if (active) setResult({ key, next: null, status: "unavailable" }); });
    return () => { active = false; controller.abort(); };
  }, [baseUrl, campaignId, token, key, loading, ready]);
  // Context changes withdraw the old verdict during render, before cleanup or
  // any asynchronous response. Nothing survives a refresh or a lost session.
  return !loading && ready && result?.key === key ? result : { next: null, status: "pending" };
}
